import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList, CommandInput } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Check, Loader2, Plus, } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import axios from "@/lib/axios"
import { faker } from "@faker-js/faker";

const CreateSecretShopperButton = ({ handleCreatedSecretShopper = () => { } }) => {
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [communities, setCommunities] = useState([])
    const [selectedCommunity, setSelectedCommunity] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { toast } = useToast()

    // Fetch communities when popover opens or search term changes
    useEffect(() => {
        if (!popoverOpen) return

        const fetchCommunities = async () => {
            setIsLoading(true)
            try {
                const { data } = await axios.get("/communities", {
                    params: {
                        secret_shopper_is_active_status: false,
                        search: searchTerm || undefined,
                    },
                })
                setCommunities(data.data)
            } catch (error) {
                toast({
                    title: "Error fetching communities",
                    variant: "destructive",
                })
                setCommunities([])
            } finally {
                setIsLoading(false)
            }
        }

        // Debounce the search
        const timeoutId = setTimeout(fetchCommunities, 300)
        return () => clearTimeout(timeoutId)
    }, [popoverOpen, searchTerm, toast])

    // Reset form when dialog closes
    useEffect(() => {
        if (!dialogOpen) {
            setSelectedCommunity(null)
        }
    }, [dialogOpen])

    const handleSelect = (community) => {
        setSelectedCommunity(community)
        setPopoverOpen(false) // Close dropdown
        setSearchTerm("") // Reset search
        setDialogOpen(true) // Open dialog
    }

    const handlePopoverOpenChange = (open) => {
        setPopoverOpen(open)
        if (!open) {
            setSearchTerm("") // Clear search when closing
        } else {
            setIsLoading(true);
        }
    }

    const generateProspectData = (community) => {
        const firstName = faker.person.firstName();
        const lastName = faker.person.lastName();
        return {
            first_name: firstName,
            last_name: lastName,
            email: faker.internet.email({
                firstName,
                lastName,
                provider: 'fizmails.com',
                options: { allowSpecialCharacters: true }
            }).toLowerCase(),
            community_id: community.id
        };
    };

    const handleSubmit = async () => {
        if (!selectedCommunity) return

        setIsSubmitting(true);

        try {
            const data = generateProspectData(selectedCommunity);
            const response = await axios.post("/secret-shopper", data);

            toast({
                title: "Secret shopper created successfully!",
                description: `Created for ${selectedCommunity.community_name}`,
            })

            handleCreatedSecretShopper(response.data);
            setDialogOpen(false) // Close dialog
        } catch (error) {
            toast({
                title: "Error creating secret shopper",
                description: "Please try again later",
                variant: "destructive",
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <Popover open={popoverOpen} onOpenChange={handlePopoverOpenChange}>
                <PopoverTrigger asChild>
                    <Button className="justify-between min-w-[200px]">
                        <Plus className="mr-2 h-4 w-4" />
                        Create Secret Shop
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0" align="end">
                    <Command>
                        <CommandInput placeholder="Search communities..." value={searchTerm} onValueChange={setSearchTerm} />
                        <CommandList>
                            {isLoading ? (
                                <div className="flex items-center justify-center py-6">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
                                </div>
                            ) : (
                                <>
                                    <CommandEmpty>No communities found.</CommandEmpty>
                                    <CommandGroup>
                                        {communities?.map((community) => (
                                            <CommandItem
                                                key={community.id}
                                                value={community.community_name}
                                                onSelect={() => handleSelect(community)}
                                                className="cursor-pointer"
                                            >
                                                <div className="flex flex-col">
                                                    <span>{community.community_name}</span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create Secret Shop</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Selected Community</label>
                                <div className="flex items-center p-3 bg-slate-50 rounded-md border">
                                    <Check className="mr-2 h-4 w-4 text-green-600" />
                                    <span className="font-medium">{selectedCommunity?.community_name}</span>
                                </div>
                            </div>
                            <div className="text-sm text-slate-600">
                                Are you sure you want to create a secret shopper for this community?
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button onClick={handleSubmit} disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                "Create Secret Shop"
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default CreateSecretShopperButton
